import { Component } from '@grafikr/components';
import { debounceTime, fromEvent } from 'rxjs';

import Openable from 'utils/components/lib/openable';
import { NonNullish } from 'utils/nullish-check';

class SearchModal extends Openable {
  protected getActiveClass() {
    return 'search-modal--active';
  }
}

export default Component((node) => {
  const announcementBar = document.querySelector<HTMLElement>('[data-announcement-bar]');
  const header = NonNullish(document.querySelector<HTMLElement>('header'));
  const searchViewToggles = NonNullish(
    header.querySelectorAll<HTMLElement>('[data-search-toggle]'),
  );
  const searchInputWrapper = NonNullish(
    header.querySelector<HTMLElement>('[data-search-input-wrapper'),
  );
  const searchInput = NonNullish(header.querySelector<HTMLInputElement>('[data-search-input]'));

  const triggers = NonNullish(
    document.querySelectorAll<HTMLButtonElement>('[data-load="search-modal"]'),
  );
  const deleteBtn = NonNullish(header.querySelector<HTMLButtonElement>('[data-clear]'));

  const modal = new SearchModal(node, { close: false, focusTrap: false, scrollLock: false });

  const initResultViewBtns = () => {
    const resultViewSectorBtns = NonNullish(
      node.querySelectorAll<HTMLButtonElement>('[data-show]'),
    );
    const resultViews = NonNullish(node.querySelectorAll<HTMLElement>('[data-view]'));

    resultViewSectorBtns.forEach((button) => {
      button.addEventListener('click', () => {
        resultViewSectorBtns.forEach((btn) => (btn.ariaSelected = 'false'));
        button.ariaSelected = 'true';

        resultViews.forEach((view) => (view.hidden = true));

        const show = button.dataset.show;
        const view = NonNullish(Array.from(resultViews).find((view) => view.dataset.view === show));
        view.hidden = false;
      });
    });
  };

  const initSubmitBtn = () => {
    NonNullish(header.querySelector<HTMLButtonElement>('[data-submit-search]')).addEventListener(
      'click',
      () => header.querySelector<HTMLFormElement>('[data-search-form]')?.submit(),
    );
  };

  const renderResults = (resultsMarkup: Element | null) => {
    if (!resultsMarkup) {
      return;
    }

    node.innerHTML = resultsMarkup.innerHTML;

    initSubmitBtn();
    initResultViewBtns();
    modal.open();
  };

  const search = async () => {
    if (!searchInput.value) {
      return;
    }

    const queryString = new URLSearchParams();
    queryString.append('q', searchInput.value);
    queryString.append('resources[type]', 'product,article,collection,page');
    queryString.append('resources[limit]', '6');
    queryString.append('resources[limit_scope]', 'each');
    queryString.append('resources[options][fields]', 'title,product_type,variants.title');
    queryString.append('section_id', 'predictive-search');

    fetch(`${window.Shopify.routes.root}search/suggest?${queryString}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status.toString());
        }

        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const resultsMarkup = parser
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        renderResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  };

  fromEvent(searchInput, 'input').pipe(debounceTime(250)).subscribe(search);

  const toggleSearchView = () => {
    document.body.classList.toggle('overflow-hidden');
    const mql = window.matchMedia('(min-width: 1024px)');

    if (!mql.matches) {
      searchInputWrapper.classList.toggle('hidden');

      if (!searchInputWrapper.classList.contains('hidden')) {
        searchInput.focus();
        if (searchInput.value) {
          const offset = header.getBoundingClientRect().bottom;
          node.style.top = `${offset}px`;
          node.style.height = `calc(100% - ${node.offsetTop}px)`;
        }
      }
    }

    searchViewToggles.forEach((toggle) => {
      if (toggle.dataset.searchToggle === 'lg') {
        if (mql.matches) {
          return;
        }
      }

      toggle.hidden = !toggle.hidden;
    });
  };

  modal.on('open', () => {
    header.classList.replace('z-40', 'z-50');
    if (announcementBar) announcementBar.classList.add('z-50');
    toggleSearchView();
  });

  modal.on('close', () => {
    header.classList.replace('z-50', 'z-40');
    if (announcementBar) announcementBar.classList.remove('z-50');
    toggleSearchView();
  });

  triggers.forEach((trigger) => trigger.addEventListener('click', modal.toggle));

  searchInput.addEventListener('focus', (event) => {
    deleteBtn.hidden = !(event.target as HTMLInputElement).value;

    if ((event.target as HTMLInputElement).value) {
      search();
    }
  });

  searchInput.addEventListener('input', (event) => {
    deleteBtn.hidden = !(event.target as HTMLInputElement).value;
  });

  deleteBtn.addEventListener('click', () => {
    searchInput.value = '';
    searchInput.focus();
    deleteBtn.hidden = true;
  });
});
